import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  useChannelActionContext,
  useChannelStateContext,
  useChatContext,
} from "stream-chat-react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { chatActionService, chatExportService } from "../../services/api";
import ChatActionModal from "./ChatActionModal";
import AddChatMembersModal from "./AddChatMembersModal";
import LeaveGroupModal from "./LeaveGroupModal";
import ReassignAdminModal from "./ReassignAdminModal";
import { useSelector } from "react-redux";
import { ROLES } from "../../config/roles";
import Avatar from "../common/Avatar";
import ClubCrest from "../common/ClubCrest";
import ClubCard from "./ClubCard";
import useChatTimer from "../../hooks/inbox/useChatTimer";
import ChatTimerChip from "./ChatTimerChip";
import DeactivateTimerModal from "./DeactivateTimerModal";
import { isClubAgentPitch } from "../../utils/chatContext";
import {
  DEFAULT_EXTENSION_CHOICES,
  stripMessagingPrefix,
} from "../../utils/chatTimer";
import { countryToFlag } from "../../utils/countryFlag";
import { useUiLanguage } from "../profile/coaching-personality/useUiLanguage";
import i18n from "../../i18n";
import { getStaticTranslation } from "../../translations/registerStaticTranslations";

// Turn a role_type slug ("sporting_director") into a display label
// ("Sporting Director"). Leaves already-formatted labels alone.
const humanizeRole = (role) => {
  const raw = String(role || "").trim();
  if (!raw) return "";
  if (/[A-Z]/.test(raw) && raw.includes(" ")) return raw;
  return raw
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const CustomChatHeader = ({ videoClient, onStartCall, onChannelLeave }) => {
  const { channel } = useChannelStateContext();
  const { client } = useChatContext();
  const navigate = useNavigate();
  // QAV5-502: window.alert() is a native browser dialog the DOM scanner
  // cannot reach — route it through the same static-bundle lookup the
  // scanner uses before it goes to the native dialog. (Vendor check: this is
  // our own error copy shown after our onStartCall callback throws, not text
  // coming from the GetStream SDK itself.)
  const currentLanguage = useUiLanguage();
  const tr = (s) => getStaticTranslation(i18n, s, currentLanguage) || s;
  // jumpToMessage (native) scrolls the stream to a message AND flashes its
  // highlight for us — so navigating matches needs zero changes to
  // CustomMessage (owned by PR#121).
  const { jumpToMessage } = useChannelActionContext();
  const [showSearchInput, setShowSearchInput] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  // Index of the currently-focused hit within searchResults (0-based).
  const [currentMatch, setCurrentMatch] = useState(0);
  const [isSearching, setIsSearching] = useState(false);
  const [showMenuPopover, setShowMenuPopover] = useState(false);
  const [modalState, setModalState] = useState({
    isOpen: false,
    type: null, // 'close', 'delete', 'archive'
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [loadingAction, setLoadingAction] = useState(null); // 'simple' or 'download'
  const [isDownloadingChat, setIsDownloadingChat] = useState(false);
  const [isReopening, setIsReopening] = useState(false);
  const [showAddMembersModal, setShowAddMembersModal] = useState(false);
  const [showLeaveGroupModal, setShowLeaveGroupModal] = useState(false);
  const [showReassignAdminModal, setShowReassignAdminModal] = useState(false);
  const [channelAdminOverride, setChannelAdminOverride] = useState(null);
  const [clubCardRect, setClubCardRect] = useState(null);
  const [showDeactivateTimerModal, setShowDeactivateTimerModal] =
    useState(false);

  const {
    user,
    roles: userRoles,
    currentRole,
  } = useSelector((state) => state.auth);
  const currentAccountId = user?.account_id ?? user?.id ?? null;
  const baseChannelAdminAccountId =
    channel?.data?.club_admin_account_id ??
    channel?.data?.pitch_details?.club_admin_account_id ??
    null;
  const normalizedCurrentAccountId =
    currentAccountId != null ? String(currentAccountId) : null;
  const normalizedBaseChannelAdminId =
    baseChannelAdminAccountId != null
      ? String(baseChannelAdminAccountId)
      : null;
  const effectiveChannelAdminId =
    channelAdminOverride ?? normalizedBaseChannelAdminId;
  const isChannelAdmin =
    Boolean(normalizedCurrentAccountId) &&
    Boolean(effectiveChannelAdminId) &&
    normalizedCurrentAccountId === effectiveChannelAdminId;

  // Check if chat is frozen (closed)
  const isFrozen = channel?.data?.frozen === true;

  // Club<->Agent lifecycle timer (both sides see remaining time; only the club
  // side gets the extend control — gate mirrored from the backend).
  const timer = useChatTimer(channel);

  const normalizeRoleToken = (role) =>
    String(role || "")
      .replace(/[^a-zA-Z0-9]/g, "")
      .toLowerCase();

  const hasClubStaffRole = useMemo(() => {
    const tokens = new Set([
      normalizeRoleToken(ROLES.ClubAdmin),
      normalizeRoleToken("club_admin"),
      normalizeRoleToken("club admin"),
      normalizeRoleToken(ROLES.Staff),
      normalizeRoleToken("staff"),
    ]);

    const rolesToCheck = [];
    if (currentRole) {
      rolesToCheck.push(currentRole);
    }
    if (Array.isArray(userRoles)) {
      rolesToCheck.push(...userRoles);
    }

    return rolesToCheck.some((role) => tokens.has(normalizeRoleToken(role)));
  }, [currentRole, userRoles]);

  const pitchId = useMemo(() => {
    if (!channel) {
      return null;
    }

    if (channel.data?.pitch_id) {
      return channel.data.pitch_id;
    }

    if (channel.data?.pitch_details?.pitch_id) {
      return channel.data.pitch_details.pitch_id;
    }

    if (channel.data?.pitch_details?.id) {
      return channel.data.pitch_details.id;
    }

    if (channel.data?.id && String(channel.data.id).includes("pitch-")) {
      return String(channel.data.id).split("pitch-")[1];
    }

    if (channel.id?.includes("pitch-")) {
      return channel.id.split("pitch-")[1];
    }

    if (channel.cid?.includes("pitch-")) {
      const segment = channel.cid.split(":").pop();
      if (segment?.includes("pitch-")) {
        return segment.split("pitch-")[1];
      }
    }

    return null;
  }, [channel]);

  // Genuine club↔agent pitch only — agent-tender response chats reuse the
  // `pitch-<id>` cid but must not offer the club-admin menu items or handover
  // modals (they are anon agent↔agent, tender_source === "agent").
  const hasPitchContext = isClubAgentPitch(channel);

  // Mobile participants-sheet gate — mirror the rail's mount condition (DV2-D):
  // ANY club↔agent channel (tp_context "club_agent" OR a genuine club-agent
  // pitch), not pitch-only, so non-pitch direct club_agent chats still get a
  // mobile way to open the sheet.
  const tpContext =
    channel?.data?._tp_custom_context?.tp_context ||
    channel?.data?.tp_context ||
    null;
  const hasParticipantsRail = hasPitchContext || tpContext === "club_agent";

  const totalChannelMembers = channel
    ? Object.keys(channel.state?.members || {}).length
    : 0;

  const canShowAddMember = hasPitchContext && isChannelAdmin && !isFrozen;
  const canShowReassignAdmin =
    hasPitchContext && isChannelAdmin && totalChannelMembers > 1;
  const canShowLeaveGroup =
    hasPitchContext && hasClubStaffRole && totalChannelMembers > 2;
  const canShowCloseChat = isChannelAdmin && !isFrozen;
  const canShowDeleteChat = isChannelAdmin;
  const canShowArchiveChat = isChannelAdmin;
  const hasTopAdminOptions = canShowAddMember || canShowReassignAdmin;
  const hasFinalAdminActions =
    canShowCloseChat || canShowDeleteChat || canShowArchiveChat;
  const shouldShowDividerBeforeLeave = hasTopAdminOptions && canShowLeaveGroup;
  const shouldShowDividerBeforeFinalAdmin =
    (hasTopAdminOptions || canShowLeaveGroup) && hasFinalAdminActions;
  const shouldShowPreArchiveDivider =
    canShowArchiveChat && (canShowCloseChat || canShowDeleteChat);

  const currentUserId = client?.userID ? String(client.userID) : null;

  const combinedOtherUsers = useMemo(() => {
    if (!channel) {
      return [];
    }

    const participantsMap = new Map();

    if (Array.isArray(channel.data?.users)) {
      channel.data.users.forEach((userEntry) => {
        const rawId = userEntry?.id;
        if (rawId === undefined || rawId === null) {
          return;
        }
        const id = String(rawId);
        if (currentUserId && id === currentUserId) {
          return;
        }
        participantsMap.set(id, {
          id,
          channelUser: userEntry,
          streamUser: null,
        });
      });
    }

    Object.values(channel.state?.members || {}).forEach((member) => {
      const streamUser = member?.user;
      if (!streamUser?.id) {
        return;
      }
      const id = String(streamUser.id);
      if (currentUserId && id === currentUserId) {
        return;
      }

      if (participantsMap.has(id)) {
        participantsMap.set(id, {
          ...participantsMap.get(id),
          streamUser,
        });
      } else {
        participantsMap.set(id, {
          id,
          channelUser: null,
          streamUser,
        });
      }
    });

    return Array.from(participantsMap.values());
  }, [channel, currentUserId]);

  const primaryParticipant = combinedOtherUsers[0] || null;

  const participantsDisplayName = useMemo(() => {
    const names = combinedOtherUsers
      .map((participant) => {
        if (!participant) {
          return null;
        }

        const { channelUser, streamUser, id } = participant;
        return (
          channelUser?.name ||
          channelUser?.full_name ||
          channelUser?.email ||
          streamUser?.name ||
          streamUser?.full_name ||
          streamUser?.email ||
          id ||
          null
        );
      })
      .filter(Boolean);

    if (!names.length) {
      return (
        channel?.data?.name ||
        channel?.data?.tender_details?.club_name ||
        "User Details Unavailable"
      );
    }

    if (names.length <= 3) {
      return names.join(", ");
    }

    const visibleNames = names.slice(0, 3);
    return `${visibleNames.join(", ")} +${
      names.length - visibleNames.length
    } more`;
  }, [combinedOtherUsers, channel]);

  const isGroupChat = combinedOtherUsers.length > 1;

  // --- Data Extraction ---
  // Avatar: Prioritize channel data profile_picture_url, then Stream state, then placeholder
  const avatarUrl =
    primaryParticipant?.channelUser?.profile_picture_url ||
    primaryParticipant?.channelUser?.image ||
    primaryParticipant?.streamUser?.image ||
    primaryParticipant?.streamUser?.profile_picture_url ||
    "/default_profile.webp";

  // Display name summarizing other participants
  const displayName = participantsDisplayName;

  // Agency/Club Name (top line in header): retains previous prioritisation based on context
  const otherUserDataFromChannelData = primaryParticipant?.channelUser;
  const otherUserFromStreamState = primaryParticipant?.streamUser;

  let agencyOrClubName = "Chat Partner"; // Default
  if (otherUserDataFromChannelData?.agency_name && !isGroupChat) {
    agencyOrClubName = otherUserDataFromChannelData.agency_name;
  } else if (otherUserDataFromChannelData?.club_name && !isGroupChat) {
    agencyOrClubName = otherUserDataFromChannelData.club_name;
  } else if (
    channel?.data?.tender_details?.club_name &&
    !isGroupChat &&
    (otherUserFromStreamState?.role?.toLowerCase().includes("agent") ||
      otherUserDataFromChannelData?.role?.toLowerCase().includes("agent"))
  ) {
    agencyOrClubName = channel.data.tender_details.club_name;
  } else if (
    channel?.data?.name &&
    channel.data.name.startsWith("Pitch:") &&
    channel.data.tender_details?.club_name
  ) {
    agencyOrClubName = channel.data.tender_details.club_name;
  } else {
    agencyOrClubName =
      (otherUserDataFromChannelData?.name && !isGroupChat
        ? otherUserDataFromChannelData.name
        : null) ||
      (otherUserFromStreamState?.name && !isGroupChat
        ? otherUserFromStreamState.name
        : null) ||
      channel?.data?.name ||
      "Details unavailable";
  }

  const derivedRole = !isGroupChat
    ? otherUserDataFromChannelData?.role || otherUserFromStreamState?.role
    : null;

  const derivedRoleLower = derivedRole?.toLowerCase() || "";
  const isAgent =
    !isGroupChat &&
    (derivedRoleLower.includes("agent") ||
      (otherUserDataFromChannelData?.email &&
        otherUserDataFromChannelData.email.toLowerCase().includes("agent")));
  const isClub =
    !isGroupChat &&
    (derivedRoleLower.includes("club") ||
      derivedRoleLower.includes("scout") ||
      (otherUserDataFromChannelData?.email &&
        otherUserDataFromChannelData.email.toLowerCase().includes("club")));

  const isOnline = Boolean(otherUserFromStreamState?.online);

  // --- Person-first identity (Turn 7 · frames 7b/7c) ---
  // The person is the anchor, the club rides beneath as one quiet line. The
  // displayed person follows the current club admin so the header stays in sync
  // after a handover (effectiveChannelAdminId already accounts for reassigns).
  const contactParticipant = useMemo(() => {
    if (effectiveChannelAdminId) {
      const match = combinedOtherUsers.find(
        (p) => String(p.id) === String(effectiveChannelAdminId)
      );
      if (match) {
        return match;
      }
    }
    return primaryParticipant;
  }, [combinedOtherUsers, effectiveChannelAdminId, primaryParticipant]);

  const contactChannelUser = contactParticipant?.channelUser;
  const contactStreamUser = contactParticipant?.streamUser;

  // Turn 7 (person-first): the person is the anchor at EVERY 1:1 counterpart —
  // agent↔agent, club→agent and club↔club alike — with the agency (agent) or the
  // club (crest line) riding beneath as the quiet second line. Group chats keep
  // the group/name anchor unchanged.
  const showPersonFirst = !isGroupChat;

  const personName =
    contactChannelUser?.name ||
    contactChannelUser?.full_name ||
    contactChannelUser?.email ||
    contactStreamUser?.name ||
    displayName;
  const personRole = humanizeRole(
    contactChannelUser?.role || contactStreamUser?.role || derivedRole
  );
  const personAvatarUrl =
    contactChannelUser?.profile_picture_url ||
    contactChannelUser?.image ||
    contactStreamUser?.image ||
    avatarUrl;

  // Club identity stamped on channel data (backend adds club_league/club_logo/
  // club_country alongside club_name/club_id).
  const clubName =
    contactChannelUser?.club_name ||
    otherUserDataFromChannelData?.club_name ||
    null;
  const clubId =
    contactChannelUser?.club_id ?? otherUserDataFromChannelData?.club_id ?? null;
  // The Wyscout team id — a DIFFERENT id space from club_id (the platform
  // users.Club PK). Only this one may be used to look a club up on the
  // Wyscout-keyed public-profile endpoint / page; club_id resolves to an
  // unrelated club there. Absent for lite/unmatched clubs, and for channels
  // stamped before the backend carried the key.
  const clubWyscoutId =
    contactChannelUser?.club_wyscout_id ??
    otherUserDataFromChannelData?.club_wyscout_id ??
    null;
  const clubLeague =
    contactChannelUser?.club_league ||
    otherUserDataFromChannelData?.club_league ||
    null;
  const clubLogo =
    contactChannelUser?.club_logo ||
    otherUserDataFromChannelData?.club_logo ||
    null;
  const clubCountry =
    contactChannelUser?.club_country ||
    otherUserDataFromChannelData?.club_country ||
    null;

  // D19: flags where the country is known. Clubs always carry club_country; a
  // person's nationality (V3) renders only when the backend stamped it (agents
  // have no source yet → no person flag, never faked).
  const clubFlag = countryToFlag(clubCountry);
  const personNationality =
    contactChannelUser?.nationality ||
    otherUserDataFromChannelData?.nationality ||
    null;
  const personFlag = countryToFlag(personNationality);

  // Agency name for the agent counterpart's quiet second line (Turn 7). Clubs
  // ride on the crest line below; agents get a plain "Agency" text line instead.
  const agencyName =
    contactChannelUser?.agency_name ||
    otherUserDataFromChannelData?.agency_name ||
    null;

  const anchorAvatarUrl = showPersonFirst ? personAvatarUrl : avatarUrl;
  const anchorName = showPersonFirst ? personName : agencyOrClubName;
  const anchorAvatarSize = showPersonFirst ? 44 : 32;

  // --- Timer rights affordances (design 3a / 1m) ---
  // The chip is shown to BOTH parties; the rights sub-label, the standalone
  // "Extend +Nd" button and the kebab TIMER section appear only for the
  // controller (backend-gated via can_extend / can_deactivate — never a role
  // guess).
  // T038: the kebab renders EVERY choice timer-status offers (default
  // [5,10,14]); the standalone header button stays a one-click shortcut for the
  // primary = last/longest choice (matching design; a role-specific
  // single-entry array uses its own value, e.g. club [5] → +5d in 5a).
  const extensionChoices =
    (Array.isArray(timer.extensionChoices) && timer.extensionChoices.length
      ? timer.extensionChoices
      : DEFAULT_EXTENSION_CHOICES);
  const primaryExtension =
    extensionChoices[extensionChoices.length - 1] || 14;
  const showTimerControls =
    timer.isTimerChat &&
    (timer.canExtend || timer.canDeactivate) &&
    !timer.isExpired &&
    !timer.isPermanent;
  // T034: a DEACTIVATED thread kept can_extend/can_deactivate false, so the
  // whole kebab TIMER section disappeared and the modal's "you can reactivate
  // anytime" promise had no affordance. `can_reactivate` (BE PR #176) is the
  // controller-only, deactivated-only capability behind that promise — it keeps
  // the section alive while the chip reads ∞ Permanent.
  const showTimerReactivate = timer.isTimerChat && timer.canReactivate;
  const showTimerMenuSection = showTimerControls || showTimerReactivate;
  // Genuine club side reads "Your club holds the timer"; the agent-accepter (BE
  // reports is_club=true for them, D-007/M4-F3) reads the personal wording.
  const timerRightsHint =
    timer.isClubSide && !timer.isAgentAgent
      ? "Your club holds the timer"
      : "You accepted — you control this timer";
  const counterpartName = showPersonFirst ? personName : displayName;
  const counterpartFirstName =
    String(counterpartName || "").trim().split(/\s+/)[0] || counterpartName;

  // T038: the menu offers every backend choice, so the extend handler takes the
  // chosen length. The header's standalone button keeps firing the primary one.
  const handleExtend = (days) => {
    if (timer.isMutating) return;
    timer.extend(days);
  };
  const handleExtendPrimary = () => handleExtend(primaryExtension);

  // T034: no confirmation modal — reactivating is the reversible, non-permanent
  // direction (deactivation keeps its friction modal). The backend picks the
  // context default window, so the entry never promises a day count.
  const handleReactivateTimer = () => {
    if (timer.isMutating) return;
    timer.reactivate();
  };

  // D14: header identity is CLICKABLE; the "View profile" ⋯-entry is removed.
  // Agency id for the agent counterpart's agency-profile link (its person name
  // deep-links to the agent profile, the agency second line to the agency).
  const agencyId =
    contactChannelUser?.agency_id ??
    otherUserDataFromChannelData?.agency_id ??
    null;

  // Person (agent) → agent profile; agency second line → agency profile; club
  // crest/name → club profile. A club STAFFER has no public route, so their name
  // opens the ClubCard popover instead (D14) — wired at the name element below.
  const goToAgentProfile = () => {
    if (!isGroupChat && contactParticipant?.id != null) {
      navigate(`/search/agent/${contactParticipant.id}`);
    }
  };
  const goToAgencyProfile = () => {
    if (agencyId != null) navigate(`/search/agency/${agencyId}`);
  };
  const goToClubProfile = () => {
    if (clubWyscoutId != null) navigate(`/search/club/${clubWyscoutId}`);
  };

  const openClubCard = (e) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setClubCardRect(rect);
  };

  // Desktop hover-open (design 6c): a short dwell on the header's club line opens
  // the card; leaving before the dwell elapses cancels the pending open. Touch
  // devices never hover, so this stays desktop-only; click still opens instantly.
  const clubHoverTimerRef = useRef(null);
  const openClubCardHover = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    clearTimeout(clubHoverTimerRef.current);
    clubHoverTimerRef.current = setTimeout(() => setClubCardRect(rect), 250);
  };
  const cancelClubCardHover = () => clearTimeout(clubHoverTimerRef.current);
  useEffect(() => () => clearTimeout(clubHoverTimerRef.current), []);

  const handleAudioCall = async () => {
    if (!videoClient || !channel) return;

    try {
      const callId = `audio-${channel.id}-${Date.now()}`;
      const call = videoClient.call("default", callId);

      console.log("Creating audio call with room-based approach");

      await call.getOrCreate({
        data: {
          // Stamp the origin chat thread onto the call. It travels to
          // everyone who joins (call.state.custom), so in-call surfaces
          // (AddParticipantOverlay) can route follow-up invitations back
          // into this thread instead of minting a new channel.
          custom: { channel_cid: stripMessagingPrefix(channel.cid) },
        },
      });

      await call.join({ create: true });

      // Send call invitation message in chat. Text is the 3e family base
      // ("Audio call"); the attachment stays type "call_invitation" so the
      // incoming-call detection (useInbox `message.new`) still fires. The
      // terminal call_outcome / call_duration_seconds are stamped by the
      // caller's client at call-end via handleEndCall — we hand it the sent
      // message id + channel_cid through onStartCall's meta arg below.
      const sent = await channel.sendMessage({
        text: `Audio call`,
        attachments: [
          {
            type: "call_invitation",
            call_id: callId,
            call_type: "audio",
            caller_id: client.userID,
            caller_name: client.user?.name || "Unknown User",
          },
        ],
      });

      onStartCall(call, "audio", {
        call_id: callId,
        message_id: sent?.message?.id || null,
        channel_cid: stripMessagingPrefix(channel.cid),
      });
    } catch (error) {
      console.error("Error starting audio call:", error);
      alert(tr("Failed to start audio call. Please try again."));
    }
  };

  const handleVideoCall = async () => {
    if (!videoClient || !channel) return;

    try {
      const callId = `video-${channel.id}-${Date.now()}`;
      const call = videoClient.call("default", callId);

      console.log("Creating video call with room-based approach");

      await call.getOrCreate({
        data: {
          // Stamp the origin chat thread onto the call. It travels to
          // everyone who joins (call.state.custom), so in-call surfaces
          // (AddParticipantOverlay) can route follow-up invitations back
          // into this thread instead of minting a new channel.
          custom: { channel_cid: stripMessagingPrefix(channel.cid) },
        },
      });

      await call.join({ create: true });

      // Send call invitation message in chat. Text is the 3e family base
      // ("Video call"); attachment stays type "call_invitation" for the
      // incoming-call detection. See the audio path above for the outcome note.
      const sent = await channel.sendMessage({
        text: `Video call`,
        attachments: [
          {
            type: "call_invitation",
            call_id: callId,
            call_type: "video",
            caller_id: client.userID,
            caller_name: client.user?.name || "Unknown User",
          },
        ],
      });

      onStartCall(call, "video", {
        call_id: callId,
        message_id: sent?.message?.id || null,
        channel_cid: stripMessagingPrefix(channel.cid),
      });
    } catch (error) {
      console.error("Error starting video call:", error);
      alert(tr("Failed to start video call. Please try again."));
    }
  };

  // "Call back" / "Retry" on a 3e call-event card (CustomMessage) reuse these
  // exact call-start handlers via a decoupled window event — CustomMessage can't
  // reach videoClient/onStartCall directly, and the same window-event pattern
  // already drives the mobile participants sheet. A ref keeps the listener on
  // the latest handlers without re-subscribing every render (no stale closure).
  const startCallRef = useRef(null);
  startCallRef.current = (callType) =>
    callType === "video" ? handleVideoCall() : handleAudioCall();
  useEffect(() => {
    const onStartCall = (event) =>
      startCallRef.current?.(event?.detail?.call_type);
    window.addEventListener("tp:threads:start-call", onStartCall);
    return () =>
      window.removeEventListener("tp:threads:start-call", onStartCall);
  }, []);

  // D15: the shared-content rail sits OUTSIDE <Channel> and can't reach the
  // native jumpToMessage; it asks for a jump via this window event (same
  // decoupling as start-call / the mobile participants sheet). Guarded so a
  // jump to a not-yet-loaded message can't throw.
  useEffect(() => {
    const onJump = (event) => {
      const id = event?.detail?.messageId;
      if (id) Promise.resolve(jumpToMessage(id)).catch(() => {});
    };
    window.addEventListener("tp:threads:jump-to-message", onJump);
    return () =>
      window.removeEventListener("tp:threads:jump-to-message", onJump);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // In-chat search (design 3c). Searches THIS channel's messages via
  // client.search scoped to channel.cid, sorted oldest → newest so the match
  // index maps top → bottom of the stream. Runs debounced from the input's
  // onChange (see effect below), not on Enter.
  const runSearch = async (rawQuery) => {
    const q = rawQuery.trim();
    if (!channel || q === "") {
      setSearchResults([]);
      setCurrentMatch(0);
      return;
    }
    setIsSearching(true);
    try {
      const results = await client.search(
        { members: { $in: [client.userID] }, cid: channel.cid },
        q,
        { limit: 25, offset: 0 },
        [{ created_at: 1 }]
      );
      const hits = results.results || [];
      setSearchResults(hits);
      // Focus the newest hit first — it sits nearest the user's current
      // position (bottom of the stream), so the opening jump barely moves.
      const startIndex = hits.length > 0 ? hits.length - 1 : 0;
      setCurrentMatch(startIndex);
      const first = hits[startIndex];
      if (first?.message?.id) {
        jumpToMessage(first.message.id);
      }
    } catch (error) {
      console.error("Error searching messages:", error);
      setSearchResults([]);
      setCurrentMatch(0);
    } finally {
      setIsSearching(false);
    }
  };

  // Debounce the live search: re-query 300ms after the last keystroke.
  // Queries under 2 chars are ignored so a single letter can't scroll the
  // stream around. Cleared immediately when the box empties.
  useEffect(() => {
    if (!showSearchInput) return undefined;
    const q = searchQuery.trim();
    if (q.length < 2) {
      setSearchResults([]);
      setCurrentMatch(0);
      return undefined;
    }
    const handle = setTimeout(() => runSearch(q), 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, showSearchInput, channel?.cid]);

  // #31 — broadcast the current in-chat-search match to the message renderers so
  // the focused bubble can draw its persistent blue outline + term highlight
  // (CustomMessage can't reach the header's search state directly; the same
  // decoupled window-event pattern already drives call-start / participants).
  useEffect(() => {
    const active = showSearchInput && searchResults.length > 0;
    const hit = active ? searchResults[currentMatch] : null;
    window.dispatchEvent(
      new CustomEvent("tp:threads:search-state", {
        detail: {
          messageId: hit?.message?.id || null,
          query: active ? searchQuery.trim() : "",
          channelCid: channel?.cid || null,
        },
      })
    );
  }, [showSearchInput, searchResults, currentMatch, searchQuery, channel?.cid]);

  // Move the current-match cursor and jump the stream to it. delta -1 = the
  // previous (older) hit, +1 = the next (newer) hit; wraps at both ends.
  const goToMatch = (delta) => {
    const n = searchResults.length;
    if (n === 0) return;
    const next = (currentMatch + delta + n) % n;
    setCurrentMatch(next);
    const hit = searchResults[next];
    if (hit?.message?.id) {
      jumpToMessage(hit.message.id);
    }
  };

  const openSearch = () => {
    setShowSearchInput(true);
    setSearchQuery("");
    setSearchResults([]);
    setCurrentMatch(0);
  };

  const closeSearch = () => {
    setShowSearchInput(false);
    setSearchQuery("");
    setSearchResults([]);
    setCurrentMatch(0);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      closeSearch();
    } else if (e.key === "Enter") {
      // Enter steps to the next match, Shift+Enter to the previous — matching
      // browser find. The debounced effect already ran the query.
      e.preventDefault();
      goToMatch(e.shiftKey ? -1 : 1);
    }
  };

  const openModal = (type) => {
    setModalState({ isOpen: true, type });
    setShowMenuPopover(false);
  };

  const closeModal = () => {
    if (!isProcessing) {
      setModalState({ isOpen: false, type: null });
    }
  };

  const handleOpenAddMembers = () => {
    if (!canShowAddMember) {
      return;
    }
    setShowAddMembersModal(true);
    setShowMenuPopover(false);
  };

  const handleOpenLeaveGroup = () => {
    if (!canShowLeaveGroup) {
      return;
    }
    setShowLeaveGroupModal(true);
    setShowMenuPopover(false);
  };

  const handleOpenReassignAdmin = () => {
    if (!canShowReassignAdmin) {
      return;
    }
    setShowReassignAdminModal(true);
    setShowMenuPopover(false);
  };

  const handleLeaveSuccess = (response) => {
    onChannelLeave?.(response);
  };

  const handleAdminReassigned = (newAdminAccountId) => {
    const normalizedNewAdminId =
      newAdminAccountId != null ? String(newAdminAccountId) : null;

    if (normalizedNewAdminId) {
      setChannelAdminOverride(normalizedNewAdminId);
      if (channel?.data) {
        channel.data.club_admin_account_id = normalizedNewAdminId;
        if (channel.data.pitch_details) {
          channel.data.pitch_details = {
            ...channel.data.pitch_details,
            club_admin_account_id: normalizedNewAdminId,
          };
        }
      }
    } else {
      setChannelAdminOverride(null);
    }

    setShowReassignAdminModal(false);
  };

  const downloadChatHistory = async () => {
    try {
      if (!channel) {
        throw new Error("No channel selected");
      }

      // Step 1: Start chat export using new API - use channel.id (backend format)
      const channelId = channel.id;
      console.log("Starting chat export for channel ID:", channelId);

      const exportResponse = await chatExportService.startExport(channelId);

      if (!exportResponse.success) {
        throw new Error(exportResponse.error || "Failed to start chat export");
      }

      const taskId = exportResponse.taskId;
      console.log("Export task ID:", taskId);

      // Step 2: Poll for export completion (every 5 seconds)
      let attempts = 0;
      const maxAttempts = 24; // ~2 minutes at 5s interval
      let isCompleted = false;

      while (attempts < maxAttempts && !isCompleted) {
        attempts += 1;
        console.log(
          `Polling export status, attempt ${attempts}/${maxAttempts}`
        );

        const statusResponse = await chatExportService.getExportStatus(
          channelId,
          taskId
        );

        if (statusResponse.success) {
          const { status } = statusResponse;

          if (status === "completed") {
            isCompleted = true;
            console.log("Export completed successfully");
            break;
          }

          if (status === "failed") {
            throw new Error("Chat export failed");
          }

          // Still pending, continue polling
          console.log("Export still pending, status:", status);
        }

        // Wait 5 seconds before next poll
        await new Promise((r) => setTimeout(r, 5000));
      }

      if (!isCompleted) {
        throw new Error(
          "Export timed out. Please try again or contact support."
        );
      }

      // Step 3: Download TXT file when completed
      console.log("Downloading TXT file...");

      const downloadResponse = await chatExportService.downloadExportText(
        channelId,
        taskId
      );

      if (!downloadResponse.success) {
        throw new Error(
          downloadResponse.error || "Failed to download chat export"
        );
      }

      // Create download link for TXT file
      const blob = new Blob([downloadResponse.blob], {
        type: "text/plain",
      });
      const txtUrl = URL.createObjectURL(blob);
      const txtLink = document.createElement("a");
      txtLink.href = txtUrl;
      txtLink.download = `chat-${channelId}-export.txt`;
      document.body.appendChild(txtLink);
      txtLink.click();
      document.body.removeChild(txtLink);
      URL.revokeObjectURL(txtUrl);

      toast.success("Chat history downloaded successfully!");
    } catch (error) {
      console.error("Error downloading chat:", error);
      toast.error(error.message || "Failed to download chat history");
      throw error;
    }
  };

  // Standalone download for EVERY channel member — hard-delete stays allowed
  // only because the counterpart can always save the history first, so this
  // must never be admin-gated (the BE export is member-gated, not admin-gated).
  // The destructive modals' Download-&-X flows stay untouched.
  const handleDownloadChat = async () => {
    if (isDownloadingChat) return;
    setShowMenuPopover(false);
    setIsDownloadingChat(true);
    try {
      await downloadChatHistory();
    } catch (error) {
      // downloadChatHistory already surfaced the error toast.
    } finally {
      setIsDownloadingChat(false);
    }
  };

  const handleCloseChat = async () => {
    setIsProcessing(true);
    setLoadingAction("simple");
    try {
      if (!channel) {
        throw new Error("No channel selected");
      }

      const result = await chatActionService.performChannelAction(
        channel.id,
        "close"
      );

      if (result.success) {
        toast.success("Chat closed successfully");
        closeModal();
      } else {
        throw new Error(result.error || "Failed to close chat");
      }
    } catch (error) {
      console.error("Error closing chat:", error);
      toast.error(error.message || "Failed to close chat");
    } finally {
      setIsProcessing(false);
      setLoadingAction(null);
    }
  };

  const handleDownloadAndCloseChat = async () => {
    setIsProcessing(true);
    setLoadingAction("download");
    try {
      // First export chat
      await downloadChatHistory();

      // Then close chat
      if (!channel) {
        throw new Error("No channel selected");
      }

      const result = await chatActionService.performChannelAction(
        channel.id,
        "close"
      );

      if (result.success) {
        toast.success("Chat exported and closed successfully");
        closeModal();
      } else {
        throw new Error(result.error || "Failed to close chat");
      }
    } catch (error) {
      console.error("Error in download and close:", error);
      toast.error(error.message || "Failed to export and close chat");
    } finally {
      setIsProcessing(false);
      setLoadingAction(null);
    }
  };

  const handleDeleteChat = async () => {
    setIsProcessing(true);
    setLoadingAction("simple");
    try {
      if (!channel) {
        throw new Error("No channel selected");
      }

      const result = await chatActionService.performChannelAction(
        channel.id,
        "delete"
      );

      if (result.success) {
        toast.success("Chat deleted successfully");
        closeModal();
        // Optionally navigate away or refresh channel list
      } else {
        throw new Error(result.error || "Failed to delete chat");
      }
    } catch (error) {
      console.error("Error deleting chat:", error);
      toast.error(error.message || "Failed to delete chat");
    } finally {
      setIsProcessing(false);
      setLoadingAction(null);
    }
  };

  const handleDownloadAndDeleteChat = async () => {
    setIsProcessing(true);
    setLoadingAction("download");
    try {
      // First export chat
      await downloadChatHistory();

      // Then delete chat
      if (!channel) {
        throw new Error("No channel selected");
      }

      const result = await chatActionService.performChannelAction(
        channel.id,
        "delete"
      );

      if (result.success) {
        toast.success("Chat exported and deleted successfully");
        closeModal();
      } else {
        throw new Error(result.error || "Failed to delete chat");
      }
    } catch (error) {
      console.error("Error in download and delete:", error);
      toast.error(error.message || "Failed to export and delete chat");
    } finally {
      setIsProcessing(false);
      setLoadingAction(null);
    }
  };

  const handleArchiveChat = async () => {
    setIsProcessing(true);
    setLoadingAction("simple");
    try {
      if (!channel) {
        throw new Error("No channel selected");
      }

      const result = await chatActionService.performChannelAction(
        channel.id,
        "archive"
      );

      if (result.success) {
        toast.success("Chat archived successfully");
        closeModal();
      } else {
        throw new Error(result.error || "Failed to archive chat");
      }
    } catch (error) {
      console.error("Error archiving chat:", error);
      toast.error(error.message || "Failed to archive chat");
    } finally {
      setIsProcessing(false);
      setLoadingAction(null);
    }
  };

  const handleDownloadAndArchiveChat = async () => {
    setIsProcessing(true);
    setLoadingAction("download");
    try {
      // First export chat
      await downloadChatHistory();

      // Then archive chat
      if (!channel) {
        throw new Error("No channel selected");
      }

      const result = await chatActionService.performChannelAction(
        channel.id,
        "archive"
      );

      if (result.success) {
        toast.success("Chat exported and archived successfully");
        closeModal();
      } else {
        throw new Error(result.error || "Failed to archive chat");
      }
    } catch (error) {
      console.error("Error in download and archive:", error);
      toast.error(error.message || "Failed to export and archive chat");
    } finally {
      setIsProcessing(false);
      setLoadingAction(null);
    }
  };

  // Get modal config based on type
  const getModalConfig = () => {
    switch (modalState.type) {
      case "close":
        return {
          title: "Close Chat",
          description:
            "Are you sure you want to close this chat? You can reopen it anytime.",
          confirmButtonText: "Close Chat",
          downloadButtonText: "Download & Close Chat",
          onConfirm: handleCloseChat,
          onDownloadAndConfirm: handleDownloadAndCloseChat,
        };
      case "delete":
        return {
          title: "Delete Chat",
          // Per-member delete (BE): only the caller's side is removed — the
          // copy must not claim it wipes the chat for both.
          description:
            "Are you sure you want to delete this chat? This removes it from your inbox and deletes your copy of the history. The other side keeps their copy and can still download it.",
          confirmButtonText: "Delete Chat",
          downloadButtonText: "Download & Delete Chat",
          onConfirm: handleDeleteChat,
          onDownloadAndConfirm: handleDownloadAndDeleteChat,
        };
      case "archive":
        return {
          title: "Archive Chat",
          description:
            "Archive this chat to keep it hidden from your main inbox.",
          confirmButtonText: "Archive Chat",
          downloadButtonText: "Download & Archive Chat",
          onConfirm: handleArchiveChat,
          onDownloadAndConfirm: handleDownloadAndArchiveChat,
        };
      default:
        return null;
    }
  };

  useEffect(() => {
    setShowMenuPopover(false);
    setShowAddMembersModal(false);
    setShowLeaveGroupModal(false);
    setShowReassignAdminModal(false);
    setChannelAdminOverride(null);
    setClubCardRect(null);
    setShowDeactivateTimerModal(false);
  }, [channel?.id]);

  if (!channel) {
    return (
      <div className="sticky top-0 z-20 h-[72px] border-b border-[#E5E7EB] px-6 flex items-center bg-white">
        <p className="text-[#6C737F]">Select a chat</p>
      </div>
    );
  }

  const modalConfig = getModalConfig();

  return (
    <>
      {/* Sticky chat header — stays pinned at the top of the messages
          pane while the conversation scrolls underneath. Solid bg-white
          + z-20 stops messages bleeding through. flex-none preserved so
          the layout still works when the parent is a flex column. */}
      <div className="flex-none sticky top-0 z-20 min-h-[72px] md:h-[72px] border-b border-[#E5E7EB] px-3 md:px-6 py-2 md:py-0 flex flex-wrap md:flex-nowrap items-center justify-between gap-y-1.5 bg-white relative">
        {/* T042-1: identity owns the row. flex-1 + min-w-0 make it the ONLY
            element that yields width, so the counterpart name can no longer
            collapse to a stub while fixed-size chrome keeps its full size
            (D18 — the person name is the anchor, never a truncated remnant). */}
        <div className="flex items-center gap-3 md:gap-6 min-w-0 flex-1 order-1">
          {isGroupChat ? (
            /* Multi-member club thread, agent's view (design frame 5d): an
               overlapping avatar stack stands in for the single avatar. Shows up
               to 3 member avatars, then a "+N" bubble. */
            <div className="flex flex-shrink-0 items-center" data-testid="chat-header-avatar-stack">
              {combinedOtherUsers.slice(0, 3).map((p, i) => {
                const pName =
                  p.channelUser?.name ||
                  p.channelUser?.email ||
                  p.streamUser?.name ||
                  p.id;
                const pAvatar =
                  p.channelUser?.profile_picture_url ||
                  p.channelUser?.image ||
                  p.streamUser?.image ||
                  null;
                return (
                  <span
                    key={p.id}
                    className="rounded-full border-2 border-white bg-white"
                    style={{ marginLeft: i === 0 ? 0 : -12, zIndex: 3 - i }}
                  >
                    <Avatar src={pAvatar} alt={pName} size={36} fallbackText={pName} />
                  </span>
                );
              })}
              {combinedOtherUsers.length > 3 && (
                <span
                  className="flex items-center justify-center rounded-full border-2 border-white bg-[#EEF0FF] text-[11px] font-bold text-[#444CE7]"
                  style={{ width: 36, height: 36, marginLeft: -12, zIndex: 0 }}
                >
                  +{combinedOtherUsers.length - 3}
                </span>
              )}
            </div>
          ) : (
            <div className="relative flex-shrink-0">
              <Avatar
                src={anchorAvatarUrl}
                alt="Current chat user"
                size={anchorAvatarSize}
                fallbackText={anchorName}
              />
              {showPersonFirst && clubName && (
                <ClubCrest
                  name={clubName || personName}
                  variant="badge"
                  avatarSize={44}
                />
              )}
              {isOnline && (
                <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></div>
              )}
            </div>
          )}
          <div className="min-w-0">
            {/* T042 gave the identity block flex-1 + min-w-0 so it is the only
                element that yields, but THIS row never got the same treatment:
                the name truncates while the role text and the Agent/Club/Closed
                pills are flex-shrink-0, so their combined width simply
                overflowed the block and printed on top of the call/video/search
                cluster ("Sporting Director" under the buttons at 360 and 390).
                Below md the row wraps instead — the pills drop to a second line
                and stay fully readable. md:flex-nowrap keeps the desktop row
                (and its fixed 72px header height) exactly as it is. */}
            <div className="flex flex-wrap md:flex-nowrap items-center gap-x-2 gap-y-1 min-w-0">
              {/* D14: person name is clickable. Agent → agent profile; club
                  staffer (no public route) → ClubCard popover; groups + others
                  stay plain. Hover shows clickability (underline + pointer). */}
              {showPersonFirst && isAgent && contactParticipant?.id != null ? (
                <button
                  type="button"
                  onClick={goToAgentProfile}
                  className="text-base font-bold text-black truncate hover:underline cursor-pointer text-left"
                  title={anchorName}
                  data-testid="chat-header-identity-name"
                >
                  {anchorName}
                </button>
              ) : showPersonFirst && isClub ? (
                <button
                  type="button"
                  onClick={openClubCard}
                  onMouseEnter={openClubCardHover}
                  onMouseLeave={cancelClubCardHover}
                  className="text-base font-bold text-black truncate hover:underline cursor-pointer text-left"
                  title={anchorName}
                  data-testid="chat-header-identity-name"
                >
                  {anchorName}
                </button>
              ) : (
                <h2
                  className="text-base font-bold text-black truncate"
                  title={anchorName}
                >
                  {anchorName}
                </h2>
              )}
              {/* D19: person nationality flag inline (only when stamped). */}
              {showPersonFirst && personFlag && (
                <span
                  className="text-sm flex-shrink-0 leading-none"
                  aria-label={personNationality ? `Nationality: ${personNationality}` : undefined}
                >
                  {personFlag}
                </span>
              )}
              {/* "N people" + info dot in the identity block (design 5d, desktop).
                  The existing mobile trigger below still carries the tap target. */}
              {isGroupChat && (
                <span
                  className="hidden md:inline-flex items-center gap-1 text-xs font-medium text-[#667085] flex-shrink-0"
                  data-testid="chat-header-people-count"
                >
                  {/* T042-4: singular at one member (T049/#216 pattern). */}
                  {combinedOtherUsers.length}{" "}
                  {combinedOtherUsers.length === 1 ? "person" : "people"}
                  <span
                    className="inline-flex items-center justify-center rounded-full border border-[#D0D5DD] text-[9px] font-bold text-[#98A2B3]"
                    style={{ width: 14, height: 14 }}
                    aria-hidden="true"
                    title="Everyone in this conversation"
                  >
                    i
                  </span>
                </span>
              )}
              {/* Role text sits next to the name for club/staff people
                  ("Sporting Director"); an agent's only role is "Agent", already
                  shown as the pill below, so we skip the text to avoid doubling. */}
              {showPersonFirst && personRole && !isAgent && (
                <span className="text-xs text-[#667085] font-medium flex-shrink-0">
                  {personRole}
                </span>
              )}
              {/* Role tags — design 7b/7c palette (Agent blue, Club amber). */}
              {isAgent && (
                <span className="px-1.5 py-px text-[10px] font-semibold bg-[#F0F6FF] border border-[#D6E6FF] text-[#1466D8] rounded-md flex-shrink-0">
                  Agent
                </span>
              )}
              {isClub && (
                <span className="px-1.5 py-px text-[10px] font-semibold bg-[#FDF1E7] border border-[#F3DDC4] text-[#B4550A] rounded-md flex-shrink-0">
                  Club
                </span>
              )}
              {isFrozen && !timer.isExpired && (
                <span className="px-1.5 py-px text-[10px] font-semibold bg-gray-100 border border-gray-200 text-gray-600 rounded-md flex-shrink-0">
                  Closed
                </span>
              )}
            </div>
            {showPersonFirst && clubName ? (
              // D14: club crest/name → club profile route.
              <button
                type="button"
                onClick={goToClubProfile}
                className="mt-1 flex items-center gap-1.5 text-xs text-[#667085] max-w-full cursor-pointer hover:underline"
                title={clubLeague ? `${clubName} · ${clubLeague}` : clubName}
                data-testid="chat-header-club-line"
              >
                <ClubCrest name={clubName} logoUrl={clubLogo} />
                <span className="truncate">
                  {clubLeague ? `${clubName} · ${clubLeague}` : clubName}
                </span>
                {/* D19: club country flag inline (5a). */}
                {clubFlag && (
                  <span
                    className="flex-shrink-0 leading-none"
                    aria-label={clubCountry ? `Country: ${clubCountry}` : undefined}
                  >
                    {clubFlag}
                  </span>
                )}
              </button>
            ) : showPersonFirst && agencyName ? (
              // Agent counterpart: agency second line → agency profile (D14).
              // Non-clickable fallback when no agency id resolved (never a dead link).
              agencyId != null ? (
                <button
                  type="button"
                  onClick={goToAgencyProfile}
                  className="text-xs text-[#6C737F] font-medium mt-1 truncate max-w-full cursor-pointer hover:underline text-left"
                  title={agencyName}
                  data-testid="chat-header-agency-line"
                >
                  {agencyName}
                </button>
              ) : (
                <p
                  className="text-xs text-[#6C737F] font-medium mt-1 truncate"
                  title={agencyName}
                >
                  {agencyName}
                </p>
              )
            ) : (
              <p
                className="text-xs text-[#6C737F] font-medium mt-1 truncate"
                title={displayName}
              >
                {displayName}
              </p>
            )}
          </div>
        </div>
        {/* Action Buttons — order per design 3a: timer chip (+ rights hint) →
            Extend → call → video → in-chat search → kebab menu. */}
        {/* In-chat search bar (design 3c). Overlays the whole header while
            active — Lupe · field · match counter · up/down · Close — so the
            identity + call/video/kebab cluster underneath is untouched. */}
        {showSearchInput && (
          <div
            className="absolute inset-0 z-30 flex items-center gap-3 bg-white px-3 md:px-6"
            data-testid="chat-search-bar"
          >
            {/* Lupe */}
            <svg
              className="w-5 h-5 text-[#667085] flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.8}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <div className="relative flex-1 min-w-0">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                placeholder="Search in this chat…"
                autoFocus
                className="w-full h-[44px] pl-3.5 pr-9 text-[15px] text-[#101828] bg-[#F9FAFB] border-[1.5px] border-[#2283FF] rounded-[10px] shadow-[0_0_0_3px_rgba(34,131,255,0.12)] focus:outline-none placeholder:text-[#98A2B3]"
                data-testid="chat-search-input"
              />
              {isSearching && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <div className="w-4 h-4 border-2 border-[#2283FF] border-t-transparent rounded-full animate-spin"></div>
                </div>
              )}
            </div>
            {/* Match counter — format "2 / 5" per copy deck 3c. */}
            {searchQuery.trim().length >= 2 && (
              <span
                className="text-[15px] font-semibold text-[#344054] tabular-nums whitespace-nowrap flex-shrink-0"
                data-testid="chat-search-counter"
              >
                {searchResults.length === 0
                  ? "0 / 0"
                  : `${currentMatch + 1} / ${searchResults.length}`}
              </span>
            )}
            {/* Up = previous (older) match, Down = next (newer). */}
            <button
              type="button"
              onClick={() => goToMatch(-1)}
              disabled={searchResults.length <= 1}
              className="w-[38px] h-[38px] flex-shrink-0 rounded-[9px] border border-[#E4E9F0] flex items-center justify-center text-[#475467] hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              aria-label="Previous match"
              data-testid="chat-search-prev"
            >
              <svg
                className="w-[15px] h-[15px]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.8}
                  d="M5 15l7-7 7 7"
                />
              </svg>
            </button>
            <button
              type="button"
              onClick={() => goToMatch(1)}
              disabled={searchResults.length <= 1}
              className="w-[38px] h-[38px] flex-shrink-0 rounded-[9px] border border-[#E4E9F0] flex items-center justify-center text-[#475467] hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              aria-label="Next match"
              data-testid="chat-search-next"
            >
              <svg
                className="w-[15px] h-[15px]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.8}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
            <button
              type="button"
              onClick={closeSearch}
              className="flex-shrink-0 px-1 text-[15px] font-semibold text-[#344054] hover:text-[#101828] transition-colors"
              data-testid="chat-search-close"
            >
              Close
            </button>
          </div>
        )}
        {/* Timer group — chip (+ rights hint) and the standalone Extend, per
            design 3a's order (timer -> Extend -> call -> video -> search ->
            kebab). T042-1: below md this is a full-width SECOND row of the
            pinned header, exactly as the mobile frame 5n ("Mobile thread —
            compact pinned header", D10) draws it, so a long chip such as
            "Expires in 6 days" never competes with the counterpart name and
            never clips. From md up nothing moves: order-2 puts it back between
            the identity and the action cluster on one 72px row. */}
        {(timer.isTimerChat || (showTimerControls && timer.canExtend)) && (
          <div className="order-3 md:order-2 w-full md:w-auto flex items-center gap-2 md:gap-0 flex-shrink-0">
            {timer.isTimerChat && (
              <div className="flex flex-col items-start md:items-end gap-0.5 mr-0.5">
                <ChatTimerChip variant="header" remaining={timer.remaining} />
                {showTimerControls && (
                  <span className="hidden sm:block text-[10px] text-[#98A2B3] whitespace-nowrap leading-none">
                    {timerRightsHint}
                  </span>
                )}
              </div>
            )}
            {/* Standalone Extend — controller only (never rendered for the
                other side, design 1m annotation). Still collapses below sm;
                the kebab TIMER section carries Extend there. */}
            {showTimerControls && timer.canExtend && (
              <button
                onClick={handleExtendPrimary}
                disabled={timer.isMutating}
                className="hidden sm:inline-flex ml-auto md:ml-2 px-3 py-[7px] rounded-lg border border-[#BBD7FF] text-[#1466D8] text-[12px] font-semibold whitespace-nowrap hover:bg-[#F0F6FF] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                data-testid="chat-timer-extend-button"
              >
                Extend +{primaryExtension}d
              </button>
            )}
          </div>
        )}
        {/* T042-1: the timer group is lifted OUT of this cluster (below) so it
            can wrap onto its own row at mobile; this cluster is the fixed-size
            chrome and never shrinks, which is why the timer pill can no longer
            be clipped at the right edge. */}
        <div className="flex items-center gap-1.5 md:gap-2 flex-shrink-0 order-2 md:order-3">
          {/* "Chat details" trigger (design 5f/5l). The desktop rail is
              `hidden lg:flex`, so below lg this control opens the rail's bottom
              sheet via a decoupled window event. The two breakpoints are a pair
              and must move together: leaving this at `md:hidden` while the rail
              starts at lg would strand 768 with neither, and the participants,
              handover and Shared section would be unreachable.
              D15: the sheet — and its Shared
              section — must be reachable on EVERY thread (incl. read-only), so the
              trigger renders for any open channel; the participants/handover part
              of the sheet stays club↔agent-gated inside the rail, non-club↔agent
              threads simply open to the Shared section. Shows the people count on
              a club↔agent thread, "Details" otherwise. */}
          {channel && (
            <button
              type="button"
              onClick={() =>
                window.dispatchEvent(
                  new CustomEvent("tp:threads:open-participants")
                )
              }
              className="lg:hidden inline-flex items-center justify-center gap-1.5 h-[34px] w-[34px] sm:w-auto sm:px-2.5 rounded-[9px] border border-[#E4E9F0] text-[12px] font-semibold text-[#475467] hover:bg-gray-50 transition-colors"
              aria-label="View chat details"
              data-testid="chat-participants-trigger"
            >
              <svg
                className="w-[15px] h-[15px] text-[#475467]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.6}
                  d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-1.13a4 4 0 10-4-4 4 4 0 004 4zm6-2a3 3 0 10-3-3"
                />
              </svg>
              {/* T042-4: singular at one member — mirrors the T049 (#216)
                  pluralisation pattern (inline count===1 ternary). */}
              <span className="hidden sm:inline">
                {hasParticipantsRail
                  ? `${combinedOtherUsers.length} ${
                      combinedOtherUsers.length === 1 ? "person" : "people"
                    }`
                  : "Details"}
              </span>
            </button>
          )}
          {/* Hide call and search buttons when chat is frozen. Handlers are
              untouched — only the chrome matches design 3a (34px square). */}
          {!isFrozen && (
            <>
              <button
                onClick={handleAudioCall}
                className="w-[34px] h-[34px] rounded-[9px] border border-[#E4E9F0] flex items-center justify-center hover:bg-gray-50 transition-colors"
                aria-label="Start audio call"
              >
                <svg
                  className="w-[15px] h-[15px] text-[#475467]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.36}
                    d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                  />
                </svg>
              </button>
              <button
                onClick={handleVideoCall}
                className="w-[34px] h-[34px] rounded-[9px] border border-[#E4E9F0] flex items-center justify-center hover:bg-gray-50 transition-colors"
                aria-label="Start video call"
              >
                <svg
                  className="w-[15px] h-[15px] text-[#475467]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.3}
                    d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                  />
                </svg>
              </button>
              <button
                onClick={() => (showSearchInput ? closeSearch() : openSearch())}
                className="w-[34px] h-[34px] rounded-[9px] border border-[#E4E9F0] flex items-center justify-center hover:bg-gray-50 transition-colors"
                aria-label="Search messages"
              >
                <svg
                  className="w-[14px] h-[14px] text-[#475467]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.6}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </button>
            </>
          )}

          <div className="relative">
            <button
              onClick={() => setShowMenuPopover(!showMenuPopover)}
              className={`w-[34px] h-[34px] rounded-[9px] border flex items-center justify-center transition-colors ${
                showMenuPopover
                  ? "border-[1.5px] border-[#2283FF] bg-[#F0F6FF]"
                  : "border-[#E4E9F0] hover:bg-gray-50"
              }`}
              aria-label="More options"
            >
              <svg
                width="16"
                height="4"
                viewBox="0 0 16 4"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M8.00012 2.8085C8.4472 2.8085 8.80964 2.44606 8.80964 1.99898C8.80964 1.55189 8.4472 1.18945 8.00012 1.18945C7.55303 1.18945 7.19059 1.55189 7.19059 1.99898C7.19059 2.44606 7.55303 2.8085 8.00012 2.8085Z"
                  stroke="#1E1E1E"
                  strokeWidth="1.3619"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M13.6668 2.8085C14.1139 2.8085 14.4763 2.44606 14.4763 1.99898C14.4763 1.55189 14.1139 1.18945 13.6668 1.18945C13.2197 1.18945 12.8573 1.55189 12.8573 1.99898C12.8573 2.44606 13.2197 2.8085 13.6668 2.8085Z"
                  stroke="#1E1E1E"
                  strokeWidth="1.3619"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M2.33345 2.8085C2.78054 2.8085 3.14297 2.44606 3.14297 1.99898C3.14297 1.55189 2.78054 1.18945 2.33345 1.18945C1.88636 1.18945 1.52393 1.55189 1.52393 1.99898C1.52393 2.44606 1.88636 2.8085 2.33345 2.8085Z"
                  stroke="#1E1E1E"
                  strokeWidth="1.3619"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>

            {/* Menu Popover */}
            {showMenuPopover && (
              <>
                {/* Backdrop */}
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setShowMenuPopover(false)}
                />

                {/* Popover Menu — design 3a: 268px, radius 12, the TIMER
                    section (rights holder only) rides on top of the existing
                    platform actions (Close / Archive / Delete / member ops). */}
                <div
                  className="absolute right-0 mt-2 bg-white rounded-[12px] border border-[#E4E9F0] z-50 overflow-hidden"
                  style={{
                    width: 268,
                    padding: 6,
                    boxShadow: "0 16px 40px rgba(16,24,40,.16)",
                  }}
                >
                  {showTimerMenuSection && (
                    <div className="mb-1" data-testid="chat-timer-menu-section">
                      <div className="px-[11px] pt-[7px] pb-1 font-mono text-[9px] font-semibold tracking-[0.06em] text-[#98A2B3]">
                        TIMER — YOU HOLD THE RIGHTS
                      </div>
                      {/* T038: one row per choice the BACKEND offers
                          (extension_choices, default [5,10,14]) instead of the
                          single hardcoded +14. Same menu-item markup as every
                          other entry — no new component family, and per D16 the
                          choices stay menu rows, never pills or tabs. */}
                      {timer.canExtend &&
                        extensionChoices.map((days) => (
                          <button
                            key={days}
                            type="button"
                            onClick={() => {
                              handleExtend(days);
                              setShowMenuPopover(false);
                            }}
                            disabled={timer.isMutating}
                            className="w-full flex items-center gap-2.5 px-[11px] py-2 rounded-lg text-left hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            data-testid={`chat-timer-extend-menu-item-${days}`}
                          >
                            <svg
                              className="w-3.5 h-3.5 text-[#475467] flex-shrink-0"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                              aria-hidden="true"
                            >
                              <circle cx="12" cy="12" r="9" strokeWidth={1.6} />
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={1.6}
                                d="M12 7.5V12l3 2"
                              />
                            </svg>
                            <span className="flex flex-col">
                              <span className="text-[13px] font-semibold text-[#101828]">
                                Extend timer{" "}
                                <span data-no-translate="true">+{days}d</span>
                              </span>
                              {/* T037: the backend ADDS to the current expiry,
                                  it does not restart from today —
                                  chat_timer.extend_timer():
                                  `base = timer.expires_at if … > now else now`
                                  and test_club_extends_each_choice pins
                                  after-before == days. The old "from today"
                                  sub-label described a behaviour that has never
                                  existed. */}
                              <span className="text-[11px] text-[#98A2B3]">
                                <span data-no-translate="true">+{days}</span>{" "}
                                days on top of the current expiry
                              </span>
                            </span>
                          </button>
                        ))}
                      {timer.canDeactivate && (
                        <button
                          type="button"
                          onClick={() => {
                            setShowDeactivateTimerModal(true);
                            setShowMenuPopover(false);
                          }}
                          className="w-full flex items-center gap-2.5 px-[11px] py-2 rounded-lg text-left bg-[#F0F6FF] hover:bg-[#E3EEFF] transition-colors"
                          data-testid="chat-timer-deactivate-menu-item"
                        >
                          <span
                            className="text-[14px] font-bold text-[#1466D8] leading-none flex-shrink-0"
                            data-no-translate="true"
                            aria-hidden="true"
                          >
                            ∞
                          </span>
                          <span className="flex flex-col">
                            <span className="text-[13px] font-semibold text-[#1466D8]">
                              Deactivate timer for this thread
                            </span>
                            <span className="text-[11px] text-[#667085]">
                              This thread never expires — other chats with{" "}
                              {counterpartFirstName} keep theirs
                            </span>
                          </span>
                        </button>
                      )}
                      {timer.canReactivate && (
                        <button
                          type="button"
                          onClick={() => {
                            handleReactivateTimer();
                            setShowMenuPopover(false);
                          }}
                          disabled={timer.isMutating}
                          className="w-full flex items-center gap-2.5 px-[11px] py-2 rounded-lg text-left bg-[#F0F6FF] hover:bg-[#E3EEFF] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          data-testid="chat-timer-reactivate-menu-item"
                        >
                          <svg
                            className="w-3.5 h-3.5 text-[#1466D8] flex-shrink-0"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            aria-hidden="true"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={1.6}
                              d="M20 12a8 8 0 11-2.34-5.66M20 4v4h-4"
                            />
                          </svg>
                          <span className="flex flex-col">
                            <span className="text-[13px] font-semibold text-[#1466D8]">
                              Reactivate timer
                            </span>
                            <span className="text-[11px] text-[#667085]">
                              Starts a fresh countdown for this thread
                            </span>
                          </span>
                        </button>
                      )}
                      <div className="h-px bg-[#F0F2F6] mx-2 my-1" />
                      {/* New topic (design 1p/3a). D-004: the mechanic needs a
                          topic-suffixed direct channel (backend) — not surgical
                          here, so the entry ships disabled-with-tooltip and the
                          mechanic is logged. */}
                      <button
                        type="button"
                        disabled
                        title="New topic is coming soon — it will start a separate thread"
                        className="w-full flex items-center gap-2.5 px-[11px] py-2 rounded-lg text-left opacity-50 cursor-not-allowed"
                        data-testid="chat-new-topic-item"
                      >
                        <svg
                          className="w-3.5 h-3.5 text-[#475467] flex-shrink-0"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          aria-hidden="true"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.6}
                            d="M12 5v14M5 12h14"
                          />
                        </svg>
                        <span className="flex flex-col">
                          <span className="text-[13px] font-semibold text-[#101828]">
                            New topic
                          </span>
                          <span className="text-[11px] text-[#98A2B3]">
                            Start a separate thread with {counterpartFirstName}
                          </span>
                        </span>
                      </button>
                      <div className="h-px bg-[#F0F2F6] mx-2 my-1" />
                    </div>
                  )}
                  <div className="py-1">
                    {/* D14/V4: the "View profile" ⋯-entry is removed — identity
                        in the header is directly clickable instead. */}
                    {canShowAddMember && (
                      <button
                        onClick={handleOpenAddMembers}
                        className="w-full px-4 py-3 text-left text-sm text-[#1E1E1E] hover:bg-gray-50 flex items-center gap-3 transition-colors"
                      >
                        <svg
                          className="w-5 h-5 text-[#6C737F]"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 4v16m8-8H4"
                          />
                        </svg>
                        <span>Add new member</span>
                      </button>
                    )}

                    {canShowReassignAdmin && (
                      <button
                        onClick={handleOpenReassignAdmin}
                        className="w-full px-4 py-3 text-left text-sm text-[#1E1E1E] hover:bg-gray-50 flex items-center gap-3 transition-colors"
                      >
                        <svg
                          className="w-5 h-5 text-[#6C737F]"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M10 6H6a2 2 0 00-2 2v8a2 2 0 002 2h4"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M14 17l5-5m0 0l-5-5m5 5H9"
                          />
                        </svg>
                        <span>Change admin</span>
                      </button>
                    )}

                    {shouldShowDividerBeforeLeave && (
                      <div className="border-t border-[#E5E7EB]" />
                    )}

                    {shouldShowDividerBeforeFinalAdmin && (
                      <div className="border-t border-[#E5E7EB]" />
                    )}

                    {/* Download for EVERY member (not admin-gated) — also on
                        frozen chats, so the counterpart can always save the
                        history before an admin hard-deletes. */}
                    <button
                      onClick={handleDownloadChat}
                      disabled={isDownloadingChat}
                      className="w-full px-4 py-3 text-left text-sm text-[#1E1E1E] hover:bg-gray-50 flex items-center gap-3 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="chat-download-menu-item"
                    >
                      <svg
                        className="w-5 h-5 text-[#6C737F]"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3"
                        />
                      </svg>
                      <span>
                        {isDownloadingChat ? "Preparing…" : "Download chat history"}
                      </span>
                    </button>

                    {!isFrozen && (
                      <button
                        onClick={() => openModal("close")}
                        className="w-full px-4 py-3 text-left text-sm text-[#1E1E1E] hover:bg-gray-50 flex items-center gap-3 transition-colors"
                      >
                        <svg
                          className="w-5 h-5 text-[#6C737F]"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                        <span>Close Chat</span>
                      </button>
                    )}

                    <button
                      onClick={() => openModal("archive")}
                      className="w-full px-4 py-3 text-left text-sm text-[#1E1E1E] hover:bg-gray-50 flex items-center gap-3 transition-colors"
                    >
                      <svg
                        className="w-5 h-5 text-[#6C737F]"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"
                        />
                      </svg>
                      <span>Archive Chat</span>
                    </button>

                    {shouldShowPreArchiveDivider && (
                      <div className="border-t border-[#E5E7EB]" />
                    )}

                    {canShowLeaveGroup && (
                      <button
                        onClick={handleOpenLeaveGroup}
                        className="w-full px-4 py-3 text-left text-sm text-[#B42318] hover:bg-[#FEF3F2] flex items-center gap-3 transition-colors"
                      >
                        <svg
                          className="w-5 h-5 text-[#6C737F]"
                          fill="none"
                          stroke="#B42318"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M10 6H6a2 2 0 00-2 2v8a2 2 0 002 2h4"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M14 17l5-5m0 0l-5-5m5 5H9"
                          />
                        </svg>
                        <span>Leave group</span>
                      </button>
                    )}

                    <button
                      onClick={() => openModal("delete")}
                      className="w-full px-4 py-3 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-3 transition-colors"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                      <span>Delete Chat</span>
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Chat Action Modal */}
      {modalConfig && (
        <ChatActionModal
          isOpen={modalState.isOpen}
          onClose={closeModal}
          onConfirm={modalConfig.onConfirm}
          onDownloadAndConfirm={modalConfig.onDownloadAndConfirm}
          title={modalConfig.title}
          description={modalConfig.description}
          confirmButtonText={modalConfig.confirmButtonText}
          downloadButtonText={modalConfig.downloadButtonText}
          isLoading={isProcessing}
          loadingAction={loadingAction}
        />
      )}

      {hasPitchContext && (
        <AddChatMembersModal
          isOpen={showAddMembersModal}
          onClose={() => setShowAddMembersModal(false)}
          pitchId={pitchId}
          channelId={channel?.id}
          channel={channel}
        />
      )}

      {hasPitchContext && (
        <LeaveGroupModal
          isOpen={showLeaveGroupModal}
          onClose={() => setShowLeaveGroupModal(false)}
          pitchId={pitchId}
          channelId={channel?.id}
          channel={channel}
          currentAccountId={currentAccountId}
          onLeaveSuccess={handleLeaveSuccess}
          isChannelAdmin={isChannelAdmin}
        />
      )}

      {hasPitchContext && (
        <ReassignAdminModal
          isOpen={showReassignAdminModal}
          onClose={() => setShowReassignAdminModal(false)}
          pitchId={pitchId}
          channelId={channel?.id}
          channel={channel}
          currentAdminAccountId={effectiveChannelAdminId}
          currentAccountId={currentAccountId}
          onAdminReassigned={handleAdminReassigned}
        />
      )}

      {clubCardRect && (
        <ClubCard
          club={{
            id: clubId,
            wyscoutId: clubWyscoutId,
            name: clubName,
            club_league: clubLeague,
            club_logo: clubLogo,
            club_country: clubCountry,
          }}
          contactPerson={{ name: personName, role: personRole }}
          triggerRect={clubCardRect}
          onClose={() => setClubCardRect(null)}
        />
      )}

      {showDeactivateTimerModal && (
        <DeactivateTimerModal
          participant={{
            name: counterpartName,
            subtitle:
              showPersonFirst && clubName
                ? clubLeague
                  ? `${clubName} · ${clubLeague}`
                  : clubName
                : agencyOrClubName,
            profile_picture_url: anchorAvatarUrl,
          }}
          thread={{
            intentLabel:
              channel?.data?.tp_intent_label ||
              channel?.data?.request_intent_label ||
              null,
            originLabel: channel?.data?.request_origin_label || null,
            currentTimerLabel:
              timer.remaining?.count != null
                ? `${timer.remaining.count}d left`
                : null,
          }}
          channelCid={channel?.cid}
          onClose={() => setShowDeactivateTimerModal(false)}
          // Route the confirm through the hook so the chip refreshes to
          // ∞ Permanent from the updated status (the modal's own service call
          // wouldn't update this header's timer state).
          onConfirm={() => timer.deactivate()}
        />
      )}
    </>
  );
};

export default CustomChatHeader;
